import React, { createContext, useState, useEffect, useContext, useRef } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  // Auto-logout configuration
  const AUTO_LOGOUT_TIME = 3 * 60 * 1000; // 3 minutes in milliseconds
  const WARNING_TIME = 30 * 1000; // Show warning 30 seconds before logout

  // Reset inactivity timer
  const resetInactivityTimer = () => {
    // Clear existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    // Hide warning if it's showing
    setShowLogoutWarning(false);

    // Only set timers if user is logged in
    if (user) {
      // Update last activity timestamp
      localStorage.setItem('lastActivity', Date.now().toString());

      // Set warning timer (2.5 minutes)
      warningTimerRef.current = setTimeout(() => {
        setShowLogoutWarning(true);
      }, AUTO_LOGOUT_TIME - WARNING_TIME);

      // Set logout timer (3 minutes)
      inactivityTimerRef.current = setTimeout(() => {
        handleAutoLogout();
      }, AUTO_LOGOUT_TIME);
    }
  };

  // Handle user activity
  const handleUserActivity = () => {
    resetInactivityTimer();
  };

  // Auto logout function
  const handleAutoLogout = () => {
    console.log('🔄 Auto-logging out due to inactivity');
    clearAllAuthData();
    setShowLogoutWarning(false);
    
    // Redirect to login page if we're on a protected route
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
      window.location.href = '/login?message=session_expired';
    }
  };

  // Continue session when user interacts
  const continueSession = () => {
    resetInactivityTimer();
  };

  // Event listeners for user activity
  useEffect(() => {
    if (user) {
      // Add event listeners for user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity, true);
      });

      // Initialize the timer
      resetInactivityTimer();

      // Cleanup function
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserActivity, true);
        });
        
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
        }
      };
    }
  }, [user]);

  // Check existing session on app load
  useEffect(() => {
    const checkExistingSession = () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const chatToken = localStorage.getItem('jwt_token');
      const chatUserId = localStorage.getItem('current_user_id');
      const lastActivity = localStorage.getItem('lastActivity');

      // Check if session has expired due to inactivity
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceLastActivity > AUTO_LOGOUT_TIME) {
          console.log('🕒 Session expired due to inactivity');
          clearAllAuthData();
          setLoading(false);
          return;
        }
      }

      // Priority: Use existing auth data first, then check chat format
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          // Ensure chat format tokens are set for compatibility
          if (!chatToken) {
            localStorage.setItem('jwt_token', token);
          }
          if (!chatUserId && parsedUser.id) {
            localStorage.setItem('current_user_id', parsedUser.id.toString());
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          clearAllAuthData();
        }
      } 
      // Fallback: Check if we have chat format data but no regular auth data
      else if (chatToken && chatUserId) {
        // Try to reconstruct user data from chat format
        const chatUserData = localStorage.getItem('user_data');
        if (chatUserData) {
          try {
            const parsedUser = JSON.parse(chatUserData);
            setUser(parsedUser);
            // Also set in regular format for compatibility
            localStorage.setItem('authToken', chatToken);
            localStorage.setItem('userData', chatUserData);
          } catch (error) {
            console.error('Error parsing chat user data:', error);
            clearAllAuthData();
          }
        }
      }
      
      setLoading(false);
    };

    checkExistingSession();
  }, []);

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.role === 'super-admin') return true;
    
    // Check permissions array from backend
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission);
    }
    
    return false;
  };

  // Check if user has any of the given permissions
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    if (user.role === 'super-admin') return true;
    
    if (user.permissions && Array.isArray(user.permissions)) {
      return permissions.some(permission => user.permissions.includes(permission));
    }
    
    return false;
  };

  // Check if user has all of the given permissions
  const hasAllPermissions = (permissions) => {
    if (!user) return false;
    if (user.role === 'super-admin') return true;
    
    if (user.permissions && Array.isArray(user.permissions)) {
      return permissions.every(permission => user.permissions.includes(permission));
    }
    
    return false;
  };

  const isAdmin = () => {
    return user?.role === 'admin' || 
           user?.role === 'super-admin' || 
           user?.role?.includes('-admin');
  };

  const isSuperAdmin = () => {
    return user?.role === 'super-admin';
  };

  const isStateAdmin = () => {
    return user?.role === 'state-admin';
  };

  const clearAllAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpires');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('current_user_id');
    localStorage.removeItem('user_data');
    localStorage.removeItem('lastActivity');
    setUser(null);
  };

  const login = (userData, token) => {
    // Store in both formats for compatibility
    // Regular app format
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    
    // Chat component format (required)
    localStorage.setItem('jwt_token', token);
    localStorage.setItem('current_user_id', userData.id.toString());
    localStorage.setItem('user_data', JSON.stringify(userData));
    
    // Set initial activity timestamp
    localStorage.setItem('lastActivity', Date.now().toString());
    
    setUser(userData);
  };

  const logout = () => {
    clearAllAuthData();
    setShowLogoutWarning(false);
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { 
      ...user, 
      ...updatedUserData
    };
    
    // Update in all storage locations
    localStorage.setItem('userData', JSON.stringify(newUserData));
    localStorage.setItem('user_data', JSON.stringify(newUserData));
    
    // Update chat format if user ID changes
    if (updatedUserData.id && updatedUserData.id !== user?.id) {
      localStorage.setItem('current_user_id', updatedUserData.id.toString());
    }
    
    setUser(newUserData);
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: isAdmin(),
    isSuperAdmin: isSuperAdmin(),
    isStateAdmin: isStateAdmin(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    showLogoutWarning,
    continueSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Auto-logout Warning Modal */}
      {showLogoutWarning && (
        <div style={logoutWarningStyles.overlay}>
          <div style={logoutWarningStyles.modal}>
            <div style={logoutWarningStyles.icon}>⚠️</div>
            <h3 style={logoutWarningStyles.title}>Session About to Expire</h3>
            <p style={logoutWarningStyles.message}>Your session will expire due to inactivity in 30 seconds.</p>
            <p style={logoutWarningStyles.message}>Would you like to continue your session?</p>
            <div style={logoutWarningStyles.actions}>
              <button 
                style={logoutWarningStyles.continueBtn} 
                onClick={continueSession}
              >
                Continue Session
              </button>
              <button 
                style={logoutWarningStyles.logoutBtn} 
                onClick={logout}
              >
                Log Out Now
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

// Inline styles for the logout warning modal
const logoutWarningStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  modal: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    animation: 'slideIn 0.3s ease-out',
  },
  icon: {
    fontSize: '48px',
    marginBottom: '15px',
  },
  title: {
    color: '#dc3545',
    marginBottom: '10px',
    fontSize: '20px',
  },
  message: {
    color: '#666',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginTop: '20px',
  },
  continueBtn: {
    background: '#28a745',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background 0.2s',
  },
  logoutBtn: {
    background: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background 0.2s',
  },
};

// Add CSS animation for the modal
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`, styleSheet.cssRules.length);

// Add hover effects
styleSheet.insertRule(`
  .continue-btn:hover {
    background: #218838 !important;
  }
`, styleSheet.cssRules.length);

styleSheet.insertRule(`
  .logout-btn:hover {
    background: #5a6268 !important;
  }
`, styleSheet.cssRules.length);

export { AuthContext };